// Helper to clean queries
const cleanQueries = (queries: (string | undefined)[]): string[] => {
  return queries.filter((q): q is string => !!q);
};

interface PodcastCandidate {
  country?: string;
  rssUrl?: string;
  title?: string;
  categories?: string[];
}

export interface CategorySpec {
  id: string;
  title: string;
  sub: string;
  queries: string[];
  seedCategories: string[];
  type?: "genre" | "curated";
  seedFilter?: (p: PodcastCandidate) => boolean;
  filterTag: string;
}

const COUNTRY_LANG_MAP: Record<string, string> = {
  Sweden: "sv",
  Finland: "fi",
  Norway: "no",
  Denmark: "da",
  Iceland: "is",
  "United Kingdom": "en",
  Germany: "de",
  France: "fr",
  Spain: "es",
  Italy: "it",
  Netherlands: "nl",
  Poland: "pl",
  Portugal: "pt",
  Russia: "ru",
  Ukraine: "uk",
  Belgium: "fr",
  Austria: "de",
  Switzerland: "de",
  Ireland: "en",
  Greece: "el",
  "Czech Republic": "cs",
  Hungary: "hu",
  Romania: "ro",
  Bulgaria: "bg",
  "United States": "en",
  Canada: "en",
  Mexico: "es",
  Brazil: "pt",
  Argentina: "es",
  Chile: "es",
  Colombia: "es",
  Peru: "es",
  Venezuela: "es",
  Australia: "en",
  "New Zealand": "en",
  Japan: "ja",
  China: "zh",
  India: "hi",
  Turkey: "tr",
  "South Korea": "ko",
  Thailand: "th",
  Vietnam: "vi",
  Indonesia: "id",
};

const TERMS: Record<string, Record<string, string>> = {
  news: {
    fi: "uutiset",
    sv: "nyheter",
    es: "noticias",
    fr: "actualités",
    de: "nachrichten",
    it: "notizie",
    pt: "notícias",
  },
  entertainment: {
    fi: "viihde",
    sv: "nöje",
    es: "entretenimiento",
    fr: "divertissement",
    de: "unterhaltung",
    it: "intrattenimento",
    pt: "entretenimento",
  },
  comedy: {
    fi: "komedia",
    sv: "humor",
    es: "comedia",
    fr: "humour",
    de: "comedy",
    it: "commedia",
    pt: "comédia",
  },
  crime: {
    fi: "rikos",
    sv: "krim",
    es: "crimen real",
    fr: "true crime",
    de: "verbrechen",
    it: "true crime",
    pt: "crimes reais",
  },
  sports: {
    fi: "urheilu",
    sv: "sport",
    es: "deportes",
    fr: "sports",
    de: "sport",
    it: "sport",
    pt: "esportes",
  },
  society: {
    fi: "yhteiskunta",
    sv: "samhälle",
    es: "sociedad",
    fr: "société",
    de: "gesellschaft",
    it: "società",
    pt: "sociedade",
  },
  fishing: {
    fi: "kalastus",
    sv: "fiske",
    es: "pesca",
    fr: "pêche",
    de: "angeln",
    it: "pesca",
    pt: "pesca",
  },
  tech: {
    fi: "teknologia",
    sv: "teknik",
    es: "tecnología",
    fr: "technologie",
    de: "technologie",
    it: "tecnologia",
    pt: "tecnologia",
  },
  history: {
    fi: "historia",
    sv: "historia",
    es: "historia",
    fr: "histoire",
    de: "geschichte",
    it: "storia",
    pt: "história",
  },
  business: {
    fi: "talous",
    sv: "ekonomi",
    es: "negocios",
    fr: "affaires",
    de: "wirtschaft",
    it: "affari",
    pt: "negócios",
  },
  food: {
    fi: "ruoka",
    sv: "matlagning",
    es: "cocina",
    fr: "cuisine",
    de: "kochen",
    it: "cucina",
    pt: "culinária",
  },
};

export const getStandardPodcastCategories = (
  country: string,
): CategorySpec[] => {
  const localizedCategories: CategorySpec[] = [];
  const lang = COUNTRY_LANG_MAP[country] || "en";

  // ... (Keep existing custom blocks for Sweden/Finland if unique, but general block below handles most)
  if (country === "Sweden") {
    localizedCategories.push(
      {
        id: "pod_morgon",
        title: "Morgonstund",
        sub: "Nyheter & briefings för dagen",
        queries: cleanQueries([
          "morgon nyheter podcast",
          "dagens nyheter podd",
          "morgonpodd",
          "P1 Morgon",
        ]),
        seedFilter: (p) =>
          p.country === "Sweden" &&
          ((p.rssUrl || "").toLowerCase().includes("sr.se") ||
            (p.categories || []).some((c: string) =>
              c.toLowerCase().includes("news"),
            )),
        seedCategories: ["news"],
        filterTag: "news",
        type: "curated",
      },
      {
        id: "pod_sr",
        title: "Sveriges Radio",
        sub: "Alla SR-poddar samlade",
        queries: cleanQueries([
          "Sveriges Radio",
          "Sveriges Radio podd",
          "P1 Morgon",
          "Ekot",
        ]),
        seedFilter: (p) =>
          p.country === "Sweden" &&
          ((p.rssUrl || "").toLowerCase().includes("sr.se") ||
            (p.title || "").toLowerCase().includes("p1") ||
            (p.title || "").toLowerCase().includes("p2") ||
            (p.title || "").toLowerCase().includes("p3") ||
            (p.title || "").toLowerCase().includes("p4")),
        type: "curated",
        seedCategories: ["society_culture"],
        filterTag: "society_culture",
      },
    );
  } else if (country === "Finland") {
    localizedCategories.push(
      {
        id: "pod_aamu",
        title: "Aamun hetki",
        sub: "Uutiset ja aamulähetykset",
        queries: cleanQueries([
          "aamu-uutiset podcast",
          "suomi uutiset podcast",
          "Yle Uutiset",
        ]),
        seedFilter: (p) =>
          p.country === "Finland" &&
          ((p.rssUrl || "").toLowerCase().includes("yle.fi") ||
            (p.categories || []).some((c: string) =>
              c.toLowerCase().includes("news"),
            )),
        type: "curated",
        seedCategories: ["news"],
        filterTag: "news",
      },
      {
        id: "pod_yle",
        title: "YLE Areena",
        sub: "Kaikki YLE-podcastit",
        queries: cleanQueries([
          "YLE Areena podcast",
          "YLE uutiset",
          "YLE radio podcast",
        ]),
        seedFilter: (p) =>
          (p.rssUrl || "").toLowerCase().includes("yle.fi") ||
          (p.title || "").toLowerCase().includes("yle"),
        type: "curated",
        seedCategories: ["society_culture"],
        filterTag: "society_culture",
      },
    );
  }

  const baseCategories: CategorySpec[] = [
    {
      id: "pod_news",
      title: "News & Politics",
      sub: "Headlines",
      queries: cleanQueries([
        TERMS.news[lang] ? `${TERMS.news[lang]} podcast` : undefined,
        "news politics",
      ]),
      seedCategories: ["news"],
      type: "genre",
      filterTag: "news",
    },
    {
      id: "pod_entertainment",
      title: "Entertainment",
      sub: "Pop Culture & Fun",
      queries: cleanQueries([
        TERMS.entertainment[lang]
          ? `${TERMS.entertainment[lang]} podcast`
          : undefined,
        "entertainment pop culture",
      ]),
      seedCategories: ["entertainment"],
      type: "genre",
      filterTag: "entertainment",
    },
    {
      id: "pod_comedy",
      title: "Comedy",
      sub: "Laugh Out Loud",
      queries: cleanQueries([
        TERMS.comedy[lang] ? `${TERMS.comedy[lang]} podcast` : undefined,
        "comedy podcast",
      ]),
      seedCategories: ["comedy"],
      type: "genre",
      filterTag: "comedy",
    },
    {
      id: "pod_crime",
      title: "True Crime",
      sub: "Detectives & Mysteries",
      queries: cleanQueries([
        TERMS.crime[lang] ? `${TERMS.crime[lang]} podcast` : undefined,
        "true crime podcast",
      ]),
      seedCategories: ["true_crime"],
      type: "genre",
      filterTag: "true_crime",
    },
    {
      id: "pod_sports",
      title: "Sports",
      sub: "Game On",
      queries: cleanQueries([
        TERMS.sports[lang] ? `${TERMS.sports[lang]} podcast` : undefined,
        "sports podcast",
      ]),
      seedCategories: ["sports"],
      type: "genre",
      filterTag: "sports",
    },
    {
      id: "pod_society",
      title: "Society & Culture",
      sub: "People & Perspectives",
      queries: cleanQueries([
        TERMS.society[lang] ? `${TERMS.society[lang]} podcast` : undefined,
        "society culture podcast",
      ]),
      seedCategories: ["society_culture"],
      type: "genre",
      filterTag: "society_culture",
    },
    {
      id: "pod_fishing",
      title: "Outdoor & Fishing",
      sub: "Nature & Adventure",
      queries: cleanQueries([
        TERMS.fishing[lang] ? `${TERMS.fishing[lang]} podcast` : undefined,
        country !== "All" ? `${country} outdoor podcast` : undefined,
        "fishing outdoor",
      ]),
      seedCategories: ["fishing_outdoor"],
      type: "genre",
      filterTag: "fishing_outdoor",
    },
    {
      id: "pod_tech",
      title: "Tech & AI",
      sub: "Future & Innovation",
      queries: cleanQueries([
        TERMS.tech[lang] ? `${TERMS.tech[lang]} podcast` : undefined,
        country !== "All" ? `${country} tech podcast` : undefined,
        "technology AI",
      ]),
      seedCategories: ["tech_ai_software"],
      type: "genre",
      filterTag: "tech_ai_software",
    },
    {
      id: "pod_history",
      title: "History & Science",
      sub: "Discover the Past & Future",
      queries: cleanQueries([
        TERMS.history[lang] ? `${TERMS.history[lang]} podcast` : undefined,
        "history science",
      ]),
      seedCategories: ["history_science"],
      type: "genre",
      filterTag: "history_science",
    },
    {
      id: "pod_business",
      title: "Economy & Business",
      sub: "Markets & Money",
      queries: cleanQueries([
        TERMS.business[lang] ? `${TERMS.business[lang]} podcast` : undefined,
        "business economy",
      ]),
      seedCategories: ["economy_business"],
      type: "genre",
      filterTag: "economy_business",
    },
    {
      id: "pod_food",
      title: "Food & Cooking",
      sub: "Culinary Journeys",
      queries: cleanQueries([
        TERMS.food[lang] ? `${TERMS.food[lang]} podcast` : undefined,
        "cooking food",
      ]),
      seedCategories: ["cooking_food_wine"],
      type: "genre",
      filterTag: "cooking_food_wine",
    },
  ];

  return [...localizedCategories, ...baseCategories];
};
